import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { forgeLegend, formatSerialNumber } from '../../services/legendApi';
import ShareModal from '../share/ShareModal';
import styles from './ForgeLegendModal.module.css';

// Multi-language labels
const FORGE_LABELS = {
    selectImageFile: { zh: '请选择图片文件', en: 'Please select an image file', de: 'Bitte wählen Sie eine Bilddatei' },
    imageTooLarge: { zh: '图片大小不能超过 10MB', en: 'Image size cannot exceed 10MB', de: 'Bildgröße darf 10MB nicht überschreiten' },
    pleaseNameLegend: { zh: '请为你的传奇命名', en: 'Please name your legend', de: 'Bitte benennen Sie Ihre Legende' },
    forgeFailed: { zh: '铸造失败', en: 'Forge failed', de: 'Schmieden fehlgeschlagen' },
    networkError: { zh: '网络错误，请重试', en: 'Network error, please retry', de: 'Netzwerkfehler, bitte erneut versuchen' },
    forgeYourLegend: { zh: '铸造你的传奇', en: 'Forge Your Legend', de: 'Schmiede deine Legende' },
    soulQuote: {
        zh: '"每一台改装车，都是骑士灵魂的延伸"',
        en: '"Every custom ride is an extension of the rider\'s soul"',
        de: '"Jedes Custom-Bike ist eine Erweiterung der Seele des Fahrers"'
    },
    clickToUploadPhoto: { zh: '点击上传你的爱车照片', en: 'Click to upload your bike photo', de: 'Klicken Sie, um Ihr Motorradfoto hochzuladen' },
    supportedFormats: { zh: '支持 JPG、PNG，最大 10MB', en: 'JPG, PNG up to 10MB', de: 'JPG, PNG bis zu 10MB' },
    nameYourLegend: { zh: '为你的传奇命名', en: 'Name Your Legend', de: 'Benennen Sie Ihre Legende' },
    exampleName: { zh: '例如：暗夜骑士', en: 'e.g., Dark Knight', de: 'z.B. Dunkler Ritter' },
    tellYourStory: { zh: '讲述你的改装故事（选填）', en: 'Tell Your Story (Optional)', de: 'Erzählen Sie Ihre Geschichte (Optional)' },
    storyPlaceholder: {
        zh: '这台车代表了什么？改装过程中有什么故事？',
        en: 'What does this bike represent? Any stories from the build?',
        de: 'Was bedeutet dieses Motorrad für Sie? Geschichten vom Umbau?'
    },
    forgeLegend: { zh: '铸造传奇', en: 'Forge Legend', de: 'Legende schmieden' },
    maybeLater: { zh: '稍后再说', en: 'Maybe Later', de: 'Vielleicht später' },
    forgingLegend: { zh: '传奇铸造中...', en: 'Forging Your Legend...', de: 'Legende wird geschmiedet...' },
    legendForged: { zh: '传奇铸造成功！', en: 'Legend Forged!', de: 'Legende geschmiedet!' },
    successMessage: {
        zh: '已加入传奇殿堂，等待骑士们的致敬！',
        en: 'Added to the Hall of Legends, awaiting salutes from fellow riders!',
        de: 'In die Ruhmeshalle aufgenommen, warte auf den Gruß der Fahrer!'
    },
    shareNow: { zh: '立即分享', en: 'Share Now', de: 'Jetzt teilen' },
    viewMyLegend: { zh: '查看我的传奇', en: 'View My Legend', de: 'Meine Legende ansehen' },
    backToConfigurator: { zh: '返回配置器', en: 'Back to Configurator', de: 'Zurück zum Konfigurator' }
};

/**
 * 铸造传奇弹窗 - 将配置发布为传奇作品
 *
 * 设计理念：
 * - 仪式感：不是"发布"，是"铸造传奇"
 * - 引导用户命名和讲述故事
 * - 支持上传自定义展示图片
 * - 成功后展示传奇卡片预览
 */
export default function ForgeLegendModal({
    isOpen,
    onClose,
    configurationId,
    configurationTitle,
    configurationImage,
    onSuccess,
}) {
    const navigate = useNavigate();
    const { user, isLoggedIn, openAuthModal } = useAuth();
    const { language } = useLanguage();
    const fileInputRef = useRef(null);

    // Helper function to get label by language
    const L = (key) => FORGE_LABELS[key]?.[language] || FORGE_LABELS[key]?.en || key;

    const [step, setStep] = useState('input'); // input | forging | success
    const [title, setTitle] = useState(configurationTitle || '');
    const [story, setStory] = useState('');
    const [customImage, setCustomImage] = useState(null); // 用户上传的图片
    const [customImagePreview, setCustomImagePreview] = useState(null); // 图片预览 URL
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [forgedLegend, setForgedLegend] = useState(null);
    const [showShareModal, setShowShareModal] = useState(false);

    // 重置状态
    useEffect(() => {
        if (isOpen) {
            setStep('input');
            setTitle(configurationTitle || '');
            setStory('');
            setCustomImage(null);
            setCustomImagePreview(null);
            setError('');
            setForgedLegend(null);
        }
    }, [isOpen, configurationTitle]);

    // 清理预览 URL
    useEffect(() => {
        return () => {
            if (customImagePreview) {
                URL.revokeObjectURL(customImagePreview);
            }
        };
    }, [customImagePreview]);

    // 未登录检查
    useEffect(() => {
        if (isOpen && !isLoggedIn) {
            openAuthModal?.();
            onClose();
        }
    }, [isOpen, isLoggedIn, openAuthModal, onClose]);

    // 处理图片选择
    const handleImageSelect = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // 验证文件类型
        if (!file.type.startsWith('image/')) {
            setError(L('selectImageFile'));
            return;
        }

        // 验证文件大小 (最大 10MB)
        if (file.size > 10 * 1024 * 1024) {
            setError(L('imageTooLarge'));
            return;
        }

        // 清理旧的预览 URL
        if (customImagePreview) {
            URL.revokeObjectURL(customImagePreview);
        }

        setCustomImage(file);
        setCustomImagePreview(URL.createObjectURL(file));
        setError('');
    }, [language, customImagePreview]);

    // 移除已选图片
    const handleRemoveImage = useCallback(() => {
        if (customImagePreview) {
            URL.revokeObjectURL(customImagePreview);
        }
        setCustomImage(null);
        setCustomImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [customImagePreview]);

    // 触发文件选择
    const handleUploadClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    // 铸造传奇
    const handleForge = useCallback(async () => {
        if (!title.trim()) {
            setError(L('pleaseNameLegend'));
            return;
        }

        setIsSubmitting(true);
        setError('');
        setStep('forging');

        try {
            // 如果有自定义图片，先转换为 base64
            let heroImageBase64 = null;
            if (customImage) {
                heroImageBase64 = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(customImage);
                });
            }

            const result = await forgeLegend({
                configuration_id: configurationId,
                title: title.trim(),
                description: story.trim() || null,
                hero_image: heroImageBase64, // 传递 base64 图片数据
            });

            if (result.success) {
                setForgedLegend(result.data);
                setStep('success');
                onSuccess?.(result.data);
            } else {
                setError(result.message || L('forgeFailed'));
                setStep('input');
            }
        } catch (err) {
            console.error('Forge failed:', err);
            setError(L('networkError'));
            setStep('input');
        } finally {
            setIsSubmitting(false);
        }
    }, [configurationId, title, story, customImage, language, onSuccess]);

    // 关闭弹窗
    const handleClose = useCallback(() => {
        if (isSubmitting) return;
        onClose();
    }, [isSubmitting, onClose]);

    // 点击遮罩关闭
    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget && !isSubmitting) {
            handleClose();
        }
    }, [handleClose, isSubmitting]);

    // 查看传奇
    const handleViewLegend = useCallback(() => {
        if (forgedLegend?.id) {
            navigate(`/legend/${forgedLegend.id}`);
            onClose();
        }
    }, [forgedLegend, navigate, onClose]);

    // 分享传奇
    const handleShare = useCallback(() => {
        setShowShareModal(true);
    }, []);

    // 关闭分享弹窗
    const handleCloseShareModal = useCallback(() => {
        setShowShareModal(false);
    }, []);

    if (!isOpen) return null;

    return (
        <div className={styles.overlay} onClick={handleOverlayClick}>
            <div className={styles.modal}>
                {/* 关闭按钮 */}
                {step !== 'forging' && (
                    <button className={styles.closeBtn} onClick={handleClose}>
                        ×
                    </button>
                )}

                {/* 输入步骤 */}
                {step === 'input' && (
                    <div className={styles.inputStep}>
                        {/* 标题 */}
                        <div className={styles.header}>
                            <h2 className={styles.title}>
                                🔥 {L('forgeYourLegend')} 🔥
                            </h2>
                            <p className={styles.subtitle}>
                                {L('soulQuote')}
                            </p>
                        </div>

                        {/* 图片上传区域 */}
                        <div className={styles.imageUploadSection}>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleImageSelect}
                                className={styles.hiddenInput}
                            />

                            {customImagePreview ? (
                                // 显示用户上传的图片
                                <div className={styles.preview}>
                                    <img src={customImagePreview} alt="Custom" />
                                    <button
                                        className={styles.removeImageBtn}
                                        onClick={handleRemoveImage}
                                        type="button"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ) : (
                                // 上传区域
                                <div
                                    className={styles.uploadArea}
                                    onClick={handleUploadClick}
                                >
                                    <div className={styles.uploadIcon}>📷</div>
                                    <p className={styles.uploadText}>
                                        {L('clickToUploadPhoto')}
                                    </p>
                                    <span className={styles.uploadHint}>
                                        {L('supportedFormats')}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* 表单 */}
                        <div className={styles.form}>
                            <div className={styles.field}>
                                <label className={styles.label}>
                                    {L('nameYourLegend')}
                                    <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    className={styles.input}
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder={L('exampleName')}
                                    maxLength={50}
                                />
                            </div>

                            <div className={styles.field}>
                                <label className={styles.label}>
                                    {L('tellYourStory')}
                                </label>
                                <textarea
                                    className={styles.textarea}
                                    value={story}
                                    onChange={(e) => setStory(e.target.value)}
                                    placeholder={L('storyPlaceholder')}
                                    maxLength={500}
                                    rows={4}
                                />
                                <span className={styles.charCount}>{story.length}/500</span>
                            </div>

                            {error && (
                                <div className={styles.error}>{error}</div>
                            )}
                        </div>

                        {/* 操作按钮 */}
                        <div className={styles.actions}>
                            <button
                                className={styles.forgeBtn}
                                onClick={handleForge}
                                disabled={isSubmitting || !title.trim()}
                            >
                                🔥 {L('forgeLegend')}
                            </button>
                            <button className={styles.laterBtn} onClick={handleClose}>
                                {L('maybeLater')}
                            </button>
                        </div>
                    </div>
                )}

                {/* 铸造中 */}
                {step === 'forging' && (
                    <div className={styles.forgingStep}>
                        <div className={styles.forgingAnimation}>
                            <div className={styles.flame}>🔥</div>
                            <div className={styles.anvil}>⚒️</div>
                        </div>
                        <h3 className={styles.forgingText}>
                            {L('forgingLegend')}
                        </h3>
                    </div>
                )}

                {/* 成功 */}
                {step === 'success' && forgedLegend && (
                    <div className={styles.successStep}>
                        {/* 成功动画 */}
                        <div className={styles.successAnimation}>
                            <span className={styles.sparkle}>✨</span>
                            <span className={styles.trophy}>🏆</span>
                            <span className={styles.sparkle}>✨</span>
                        </div>

                        <h2 className={styles.successTitle}>
                            {L('legendForged')}
                        </h2>

                        <div className={styles.legendInfo}>
                            <span className={styles.legendTitle}>"{forgedLegend.title}"</span>
                            <span className={styles.serialNumber}>
                                {formatSerialNumber(forgedLegend.serial_number || forgedLegend.id)}
                            </span>
                        </div>

                        <p className={styles.successMessage}>
                            {L('successMessage')}
                        </p>

                        <div className={styles.successActions}>
                            <button className={styles.shareBtn} onClick={handleShare}>
                                🔄 {L('shareNow')}
                            </button>
                            <button className={styles.viewBtn} onClick={handleViewLegend}>
                                👁️ {L('viewMyLegend')}
                            </button>
                            <button className={styles.doneBtn} onClick={handleClose}>
                                {L('backToConfigurator')}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* 分享弹窗 */}
            <ShareModal
                isOpen={showShareModal}
                onClose={handleCloseShareModal}
                legendId={forgedLegend?.id}
                legendTitle={forgedLegend?.title}
                legendImage={configurationImage}
            />
        </div>
    );
}
